import React, { useState, useEffect } from "react";
import { collection, addDoc, doc, getDoc, updateDoc } from "firebase/firestore";
import { db, auth } from "./firebase";
import jsPDF from "jspdf";
import "jspdf-autotable";
import "./MarketingOrderForm.css";

function MarketingOrderForm({ onOrderSubmitted, editOrderId = null }) {
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    // Section A
    colourBrochuresQty: "",

    // Section B - Fee slip info
    feeSlipName: "",
    feeSlipLandline: "",
    feeSlipCellphone: "",
    feeSlipEmail: "",
    feeSlipBank: "",
    feeSlipBeneficiary: "",
    feeSlipAccountNumber: "",
    feeSlipBranchCode: "",
    feeSlipReference: "",

    // Section C1 - 4 Term schools
    c1TermFee: "",
    c1InstalmentFee: "",
    c1Qty: "",
    c1Extra: "",

    // Section C2 - 3 Term schools
    c2TermFee: "",
    c2InstalmentFee: "",
    c2Qty: "",
    c2Extra: "",

    // Section C3 - Other fee structures
    c3TermFee: "",
    c3InstalmentFee: "",
    c3Qty: "",
    c3Extra: "",

    // Section D - Other materials
    complimentSlipsQty: "",
    marketingLettersQty: "",
    postersQty: "",

    // Delivery
    deliveryMethod: "courier",
    deliveryStreet: "",
    deliveryStreet2: "",
    deliveryCity: "",
    deliveryProvince: "",
    deliveryPostalCode: "",
  });

  useEffect(() => {
    if (editOrderId) {
      loadOrderForEdit();
    }
  }, [editOrderId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadOrderForEdit = async () => {
    try {
      // Safety check - if no editOrderId, don't try to load
      if (!editOrderId) {
        console.log('No order ID provided - creating new order');
        return;
      }

      const orderDoc = await getDoc(doc(db, "marketingOrders", editOrderId));
      
      if (orderDoc.exists()) {
        const data = orderDoc.data();
        setFormData({
          colourBrochuresQty: data.colourBrochuresQty || "",
          feeSlipName: data.feeSlipName || "",
          feeSlipLandline: data.feeSlipLandline || "",
          feeSlipCellphone: data.feeSlipCellphone || "",
          feeSlipEmail: data.feeSlipEmail || "",
          feeSlipBank: data.feeSlipBank || "",
          feeSlipBeneficiary: data.feeSlipBeneficiary || "",
          feeSlipAccountNumber: data.feeSlipAccountNumber || "",
          feeSlipBranchCode: data.feeSlipBranchCode || "",
          feeSlipReference: data.feeSlipReference || "",
          c1TermFee: data.c1TermFee || "",
          c1InstalmentFee: data.c1InstalmentFee || "",
          c1Qty: data.c1Qty || "",
          c1Extra: data.c1Extra || "",
          c2TermFee: data.c2TermFee || "",
          c2InstalmentFee: data.c2InstalmentFee || "",
          c2Qty: data.c2Qty || "",
          c2Extra: data.c2Extra || "",
          c3TermFee: data.c3TermFee || "",
          c3InstalmentFee: data.c3InstalmentFee || "",
          c3Qty: data.c3Qty || "",
          c3Extra: data.c3Extra || "",
          complimentSlipsQty: data.complimentSlipsQty || "",
          marketingLettersQty: data.marketingLettersQty || "",
          postersQty: data.postersQty || "",
          deliveryMethod: data.deliveryMethod || "courier",
          deliveryStreet: data.deliveryStreet || "",
          deliveryStreet2: data.deliveryStreet2 || "",
          deliveryCity: data.deliveryCity || "",
          deliveryProvince: data.deliveryProvince || "",
          deliveryPostalCode: data.deliveryPostalCode || "",
        });
        setIsEditMode(true);
      } else {
        console.error('Order document not found');
        alert('Order not found');
      }
    } catch (error) {
      console.error("Error loading order:", error);
      alert("Error loading order for editing");
    }
  };

  const generatePDF = (orderData) => {
    const doc = new jsPDF();
    const user = auth.currentUser;

    // Title
    doc.setFontSize(20);
    doc.setTextColor(65, 105, 225);
    doc.text("Marketing Material Order Form", 105, 25, { align: "center" });

    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text(`Order Date: ${new Date().toLocaleDateString()}`, 15, 45);
    doc.text(`Email: ${user.email}`, 15, 50);

    let yPos = 60;

    // Section A
    if (orderData.colourBrochuresQty) {
      doc.setFontSize(14);
      doc.text("A) Colour Brochures", 15, yPos);
      yPos += 7;
      doc.setFontSize(10);
      doc.text(`Quantity: ${orderData.colourBrochuresQty}`, 20, yPos);
      yPos += 10;
    }

    // Section B
    if (orderData.feeSlipName) {
      doc.setFontSize(14);
      doc.text("B) Fee Slip Information", 15, yPos);
      yPos += 7;
      doc.setFontSize(10);
      doc.text(`Name: ${orderData.feeSlipName}`, 20, yPos);
      yPos += 5;
      doc.text(`Landline: ${orderData.feeSlipLandline || "N/A"}`, 20, yPos);
      yPos += 5;
      doc.text(
        `Cell: ${orderData.feeSlipCellphone || "N/A"}`,
        20,
        yPos
      );
      yPos += 5;
      doc.text(`Email: ${orderData.feeSlipEmail || "N/A"}`, 20, yPos);
      yPos += 5;
      doc.text(`Bank: ${orderData.feeSlipBank || "N/A"}`, 20, yPos);
      yPos += 5;
      doc.text(
        `Beneficiary: ${orderData.feeSlipBeneficiary || "N/A"}`,
        20,
        yPos
      );
      yPos += 5;
      doc.text(
        `Account: ${orderData.feeSlipAccountNumber || "N/A"}`,
        20,
        yPos
      );
      yPos += 5;
      doc.text(
        `Branch Code: ${orderData.feeSlipBranchCode || "N/A"}`,
        20,
        yPos
      );
      yPos += 5;
      doc.text(
        `Reference: ${orderData.feeSlipReference || "N/A"}`,
        20,
        yPos
      );
      yPos += 10;
    }

    // Section C - Fee structures
    doc.setFontSize(14);
    doc.text("C) Fee Structures", 15, yPos);
    yPos += 7;
    doc.setFontSize(10);

    if (orderData.c1Qty) {
      doc.text("4 Term Schools:", 20, yPos);
      yPos += 5;
      doc.text(
        `  Term Fee: R${orderData.c1TermFee}, Instalment: R${orderData.c1InstalmentFee}, Qty: ${orderData.c1Qty}`,
        20,
        yPos
      );
      if (orderData.c1Extra) {
        yPos += 5;
        doc.text(`  Extra: ${orderData.c1Extra}`, 20, yPos);
      }
      yPos += 7;
    }

    if (orderData.c2Qty) {
      doc.text("3 Term Schools:", 20, yPos);
      yPos += 5;
      doc.text(
        `  Term Fee: R${orderData.c2TermFee}, Instalment: R${orderData.c2InstalmentFee}, Qty: ${orderData.c2Qty}`,
        20,
        yPos
      );
      if (orderData.c2Extra) {
        yPos += 5;
        doc.text(`  Extra: ${orderData.c2Extra}`, 20, yPos);
      }
      yPos += 7;
    }

    if (orderData.c3Qty) {
      doc.text("Other Fee Structures:", 20, yPos);
      yPos += 5;
      doc.text(
        `  Term Fee: R${orderData.c3TermFee}, Instalment: R${orderData.c3InstalmentFee}, Qty: ${orderData.c3Qty}`,
        20,
        yPos
      );
      if (orderData.c3Extra) {
        yPos += 5;
        doc.text(`  Extra: ${orderData.c3Extra}`, 20, yPos);
      }
      yPos += 7;
    }

    // Section D
    if (
      orderData.complimentSlipsQty ||
      orderData.marketingLettersQty ||
      orderData.postersQty
    ) {
      yPos += 3;
      doc.setFontSize(14);
      doc.text("D) Other Materials", 15, yPos);
      yPos += 7;
      doc.setFontSize(10);
      if (orderData.complimentSlipsQty) {
        doc.text(`Compliment Slips: ${orderData.complimentSlipsQty}`, 20, yPos);
        yPos += 5;
      }
      if (orderData.marketingLettersQty) {
        doc.text(
          `Marketing Letters: ${orderData.marketingLettersQty}`,
          20,
          yPos
        );
        yPos += 5;
      }
      if (orderData.postersQty) {
        doc.text(`Posters: ${orderData.postersQty}`, 20, yPos);
        yPos += 5;
      }
    }

    // Delivery
    yPos += 5;
    doc.setFontSize(14);
    doc.text("Delivery Information", 15, yPos);
    yPos += 7;
    doc.setFontSize(10);
    doc.text(
      `Method: ${
        orderData.deliveryMethod === "courier"
          ? "Courier"
          : "Collection in Randburg"
      }`,
      20,
      yPos
    );
    yPos += 5;

    if (orderData.deliveryMethod === "courier" && orderData.deliveryStreet) {
      doc.text(`Address: ${orderData.deliveryStreet}`, 20, yPos);
      yPos += 5;
      if (orderData.deliveryStreet2) {
        doc.text(`${orderData.deliveryStreet2}`, 20, yPos);
        yPos += 5;
      }
      doc.text(
        `${orderData.deliveryCity}, ${orderData.deliveryProvince} ${orderData.deliveryPostalCode}`,
        20,
        yPos
      );
    }

    // Save
    doc.save(`BABS_Order_${new Date().toISOString().split("T")[0]}.pdf`);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    console.log('🛠 Input name:', name);
    console.log('🛠 Value typed:', value);
    
    setFormData((prev) => {
      const newData = {
        ...prev,
        [name]: value,
      };
      
      console.log('🛠 New state for', name, ':', newData[name]);
      
      return newData;
    });
  };

  // 🆕 NEW FUNCTION: Auto-create term structure templates
  const createTermStructureTemplates = async (orderData, franchiseeRef) => {
    try {
      console.log('🎯 Creating term structure templates from marketing order...');
      
      const templatesCreated = [];
      
      // C1 - 4 Term Schools
      if (orderData.c1TermFee || orderData.c1InstalmentFee) {
        const c1Template = {
          franchiseeRef: franchiseeRef,
          configType: 'termStructure',
          isActive: true,
          templateName: '4 Term Schools (c1)',
          templateDescription: orderData.c1Extra || 'Standard 4-term fee structure from marketing order',
          termType: 4,
          linkedToMarketingOrder: 'c1',
          lessonsPerTerm: 10,
          termDates: {
            term1: { start: '', end: '' },
            term2: { start: '', end: '' },
            term3: { start: '', end: '' },
            term4: { start: '', end: '' }
          },
          fees: {
            termFee: parseFloat(orderData.c1TermFee) || 0,
            instalmentFee: parseFloat(orderData.c1InstalmentFee) || 0
          },
          usedBySchoolCount: 0,
          createdDate: new Date(),
          createdBy: auth.currentUser.email,
          lastModified: new Date(),
          modifiedBy: auth.currentUser.email,
          autoCreated: true,
          sourceOrderQty: parseInt(orderData.c1Qty) || 0
        };
        
        const c1Doc = await addDoc(collection(db, 'configurations'), c1Template);
        templatesCreated.push({ id: c1Doc.id, name: '4 Term Schools (c1)' });
        console.log('✅ Created c1 template:', c1Doc.id);
      }
      
      // C2 - 3 Term Schools
      if (orderData.c2TermFee || orderData.c2InstalmentFee) {
        const c2Template = {
          franchiseeRef: franchiseeRef,
          configType: 'termStructure',
          isActive: true,
          templateName: '3 Term Schools (c2)',
          templateDescription: orderData.c2Extra || 'Standard 3-term fee structure from marketing order',
          termType: 3,
          linkedToMarketingOrder: 'c2',
          lessonsPerTerm: 10,
          termDates: {
            term1: { start: '', end: '' },
            term2: { start: '', end: '' },
            term3: { start: '', end: '' }
          },
          fees: {
            termFee: parseFloat(orderData.c2TermFee) || 0,
            instalmentFee: parseFloat(orderData.c2InstalmentFee) || 0
          },
          usedBySchoolCount: 0,
          createdDate: new Date(),
          createdBy: auth.currentUser.email,
          lastModified: new Date(),
          modifiedBy: auth.currentUser.email,
          autoCreated: true,
          sourceOrderQty: parseInt(orderData.c2Qty) || 0
        };
        
        const c2Doc = await addDoc(collection(db, 'configurations'), c2Template);
        templatesCreated.push({ id: c2Doc.id, name: '3 Term Schools (c2)' });
        console.log('✅ Created c2 template:', c2Doc.id);
      }
      
      // C3 - Other Fee Structure
      if (orderData.c3TermFee || orderData.c3InstalmentFee) {
        const c3Template = {
          franchiseeRef: franchiseeRef,
          configType: 'termStructure',
          isActive: true,
          templateName: 'Other Fee Structure (c3)',
          templateDescription: orderData.c3Extra || 'Custom fee structure from marketing order',
          termType: orderData.c3TermType || 4,
          linkedToMarketingOrder: 'c3',
          lessonsPerTerm: 10,
          termDates: {
            term1: { start: '', end: '' },
            term2: { start: '', end: '' },
            term3: { start: '', end: '' },
            term4: { start: '', end: '' }
          },
          fees: {
            termFee: parseFloat(orderData.c3TermFee) || 0,
            instalmentFee: parseFloat(orderData.c3InstalmentFee) || 0
          },
          usedBySchoolCount: 0,
          createdDate: new Date(),
          createdBy: auth.currentUser.email,
          lastModified: new Date(),
          modifiedBy: auth.currentUser.email,
          autoCreated: true,
          sourceOrderQty: parseInt(orderData.c3Qty) || 0
        };
        
        const c3Doc = await addDoc(collection(db, 'configurations'), c3Template);
        templatesCreated.push({ id: c3Doc.id, name: 'Other Fee Structure (c3)' });
        console.log('✅ Created c3 template:', c3Doc.id);
      }
      
      console.log('🎉 All templates created:', templatesCreated);
      return templatesCreated;
      
    } catch (error) {
      console.error('❌ Error creating term structure templates:', error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, "Franchisees", user.uid);

      const orderData = {
        franchiseeRef: franchiseeRef,
        orderDate: new Date(),
        year: new Date().getFullYear(),
        status: "pending",
        ...formData,
      };

      if (isEditMode && editOrderId) {
        // Update existing order
        await updateDoc(doc(db, "marketingOrders", editOrderId), orderData);
        alert("Order updated successfully!");
      } else {
        // Create new order
        await addDoc(collection(db, "marketingOrders"), orderData);
        
        // 🆕 NEW: Auto-create term structure templates
        try {
          const templates = await createTermStructureTemplates(formData, franchiseeRef);
          console.log('✅ Auto-created templates:', templates);
          alert(`Order submitted successfully! ${templates.length} term structure template(s) have been created automatically.`);
        } catch (templateError) {
          console.error('Error creating templates:', templateError);
          alert("Order submitted successfully, but there was an issue creating term structure templates. You can create them manually in Term Structure Setup.");
        }
      }

      // Generate PDF
      generatePDF(formData);

      onOrderSubmitted();
    } catch (error) {
      console.error("Error submitting order:", error);
      alert("Error submitting order. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="order-form-container">
      <div className="order-form">
        <div className="form-header">
          <img src="/arty.png" alt="BABS Logo" style={{ width: "80px" }} />
          <h1>
            {isEditMode
              ? "Edit Marketing Material Order"
              : "Marketing Material Order Form"}
          </h1>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Section A - Colour Brochures */}
          <div className="form-section">
            <h2>A) Colour Brochures</h2>
            <div className="form-group">
              <label>Quantity colour brochures:</label>
              <input
                type="number"
                name="colourBrochuresQty"
                value={formData.colourBrochuresQty}
                onChange={handleChange}
                min="0"
              />
            </div>
          </div>

          {/* Section B - Fee Slip Information */}
          <div className="form-section">
            <h2>B) Fee Slip for Parents</h2>
            <p className="section-note">
              Please complete ALL the details your parents will need to
              successfully pay you or register for a Debit Order.
            </p>

            <div className="form-group">
              <label>Name and Surname: *</label>
              <input
                type="text"
                name="feeSlipName"
                value={formData.feeSlipName}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Landline:</label>
                <input
                  type="tel"
                  name="feeSlipLandline"
                  value={formData.feeSlipLandline}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Cell phone no.:</label>
                <input
                  type="tel"
                  name="feeSlipCellphone"
                  value={formData.feeSlipCellphone}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-group">
              <label>E-mail address:</label>
              <input
                type="email"
                name="feeSlipEmail"
                value={formData.feeSlipEmail}
                onChange={handleChange}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Bank:</label>
                <input
                  type="text"
                  name="feeSlipBank"
                  value={formData.feeSlipBank}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Beneficiary name:</label>
                <input
                  type="text"
                  name="feeSlipBeneficiary"
                  value={formData.feeSlipBeneficiary}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Account number:</label>
                <input
                  type="text"
                  name="feeSlipAccountNumber"
                  value={formData.feeSlipAccountNumber}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Branch code:</label>
                <input
                  type="text"
                  name="feeSlipBranchCode"
                  value={formData.feeSlipBranchCode}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Reference parents must type:</label>
              <input
                type="text"
                name="feeSlipReference"
                value={formData.feeSlipReference}
                onChange={handleChange}
                placeholder="e.g., Student Name"
              />
            </div>
          </div>

          {/* Section C - Fee Structures */}
          <div className="form-section">
            <h2>C) Fee Structures</h2>

            {/* C1 - 4 Term Schools */}
            <div className="fee-structure-group">
              <h3>C1) 4 Term schools</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Term Fee:</label>
                  <input
                    type="number"
                    name="c1TermFee"
                    value={formData.c1TermFee}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    placeholder="e.g., 750"
                  />
                </div>
                <div className="form-group">
                  <label>Instalment fee:</label>
                  <input
                    type="number"
                    name="c1InstalmentFee"
                    value={formData.c1InstalmentFee}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    placeholder="e.g., 200"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Qty to order:</label>
                  <input
                    type="number"
                    name="c1Qty"
                    value={formData.c1Qty}
                    onChange={handleChange}
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label>Extra you want to add:</label>
                  <input
                    type="text"
                    name="c1Extra"
                    value={formData.c1Extra}
                    onChange={handleChange}
                    placeholder="Optional notes"
                  />
                </div>
              </div>
            </div>

            {/* C2 - 3 Term Schools */}
            <div className="fee-structure-group">
              <h3>C2) 3 Term schools</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Term Fee:</label>
                  <input
                    type="number"
                    name="c2TermFee"
                    value={formData.c2TermFee}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="form-group">
                  <label>Instalment Fee:</label>
                  <input
                    type="number"
                    name="c2InstalmentFee"
                    value={formData.c2InstalmentFee}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Qty to order:</label>
                  <input
                    type="number"
                    name="c2Qty"
                    value={formData.c2Qty}
                    onChange={handleChange}
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label>Extra you want to add:</label>
                  <input
                    type="text"
                    name="c2Extra"
                    value={formData.c2Extra}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>

            {/* C3 - Other Fee Structures */}
            <div className="fee-structure-group">
              <h3>C3) Other fee structures</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Term Fee:</label>
                  <input
                    type="number"
                    name="c3TermFee"
                    value={formData.c3TermFee}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="form-group">
                  <label>Instalment Fee:</label>
                  <input
                    type="number"
                    name="c3InstalmentFee"
                    value={formData.c3InstalmentFee}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Quantity to order:</label>
                  <input
                    type="number"
                    name="c3Qty"
                    value={formData.c3Qty}
                    onChange={handleChange}
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label>Extra you want to add:</label>
                  <input
                    type="text"
                    name="c3Extra"
                    value={formData.c3Extra}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Section D - Other Materials */}
          <div className="form-section">
            <h2>D) Other Materials</h2>
            <div className="form-row">
              <div className="form-group">
                <label>Compliment slips Qty:</label>
                <input
                  type="number"
                  name="complimentSlipsQty"
                  value={formData.complimentSlipsQty}
                  onChange={handleChange}
                  min="0"
                />
              </div>
              <div className="form-group">
                <label>Marketing Letters Qty:</label>
                <input
                  type="number"
                  name="marketingLettersQty"
                  value={formData.marketingLettersQty}
                  onChange={handleChange}
                  min="0"
                />
              </div>
            </div>
            <div className="form-group">
              <label>Posters Qty:</label>
              <input
                type="number"
                name="postersQty"
                value={formData.postersQty}
                onChange={handleChange}
                min="0"
              />
            </div>
          </div>

          {/* Delivery */}
          <div className="form-section">
            <h2>Delivery Information</h2>
            <div className="form-group">
              <label>Please select one: *</label>
              <select
                name="deliveryMethod"
                value={formData.deliveryMethod}
                onChange={handleChange}
                required
              >
                <option value="courier">Please courier to me</option>
                <option value="collect">
                  I will collect my order in Randburg, Gauteng
                </option>
              </select>
            </div>

            {formData.deliveryMethod === "courier" && (
              <div className="delivery-address">
                <div className="form-group">
                  <label>Street Address: *</label>
                  <input
                    type="text"
                    name="deliveryStreet"
                    value={formData.deliveryStreet}
                    onChange={handleChange}
                    required={formData.deliveryMethod === "courier"}
                  />
                </div>
                <div className="form-group">
                  <label>Street Address Line 2:</label>
                  <input
                    type="text"
                    name="deliveryStreet2"
                    value={formData.deliveryStreet2}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>City:</label>
                    <input
                      type="text"
                      name="deliveryCity"
                      value={formData.deliveryCity}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>State / Province:</label>
                    <input
                      type="text"
                      name="deliveryProvince"
                      value={formData.deliveryProvince}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Postal / Zip Code:</label>
                  <input
                    type="text"
                    name="deliveryPostalCode"
                    value={formData.deliveryPostalCode}
                    onChange={handleChange}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Confirmation */}
          <div className="form-section confirmation-section">
            <h3>
              I confirm by sending and/or submitting this Order Form that:
            </h3>
            <ol>
              <li>I checked my order and it is correct.</li>
              <li>
                I will pay for any shortfall on my Brand Building account on
                receipt of my Brand Building statement.
              </li>
              <li>
                I will pay for any courier fees on receipt of the invoice.
              </li>
            </ol>
          </div>

          <button type="submit" className="submit-button" disabled={loading}>
            {loading
              ? "Submitting..."
              : isEditMode
              ? "Update Order"
              : "Submit Order"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default MarketingOrderForm;